import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router";
import { useApi, useApiMutation } from "@/hooks/useApi";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { IconPicker } from "@/components/shared/IconPicker";
import { LoadingSpinner } from "@/components/shared/LoadingSpinner";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { QRScanner } from "@/components/shared/QRScanner";
import { showToast } from "@/components/shared/Toast";
import { QRCodeSVG } from "qrcode.react";
import {
  Save,
  ArrowLeft,
  UserCog,
  Key,
  Copy,
  RefreshCw,
  QrCode,
  Check,
  ShieldCheck,
  ShieldOff,
  Upload,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import {
  generateEncryptionKey,
  getClientKey,
  setClientKey,
  isValidEncryptionKey,
} from "@/lib/crypto";

interface ClientDetail {
  _key: string;
  name: string;
  icon: string;
  description: string;
  is_active: boolean;
  has_passcode: boolean;
  points: number;
}

export function ClientEdit() {
  const { jwt, clientId } = useParams();
  const navigate = useNavigate();

  const { data: client, loading, error } = useApi<ClientDetail>(
    `/api/admin/${jwt}/clients/${clientId}`,
  );

  const { mutate, loading: saving, error: saveError } = useApiMutation<
    Partial<ClientDetail>,
    ClientDetail
  >(`/api/admin/${jwt}/clients/${clientId}`);

  // Form state
  const [name, setName] = useState("");
  const [icon, setIcon] = useState("🧑");
  const [description, setDescription] = useState("");
  const [isActive, setIsActive] = useState(true);

  // Encryption key state
  const [encKey, setEncKey] = useState<string | null>(null);
  const [showQR, setShowQR] = useState(false);
  const [copied, setCopied] = useState(false);
  const [confirmRegenerate, setConfirmRegenerate] = useState(false);
  const [keyInput, setKeyInput] = useState("");
  const [showScanner, setShowScanner] = useState(false);
  const [showImportSection, setShowImportSection] = useState(false);

  useEffect(() => {
    if (client) {
      setName(client.name);
      setIcon(client.icon);
      setDescription(client.description);
      setIsActive(client.is_active);
    }
  }, [client]);

  useEffect(() => {
    if (clientId) {
      setEncKey(getClientKey(clientId));
    }
  }, [clientId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      showToast.error("Name is required");
      return;
    }

    const result = await mutate(
      { name: name.trim(), icon, description, is_active: isActive },
      "PUT",
    );
    if (result) {
      showToast.success("Partner updated successfully");
      navigate(`/admin/${jwt}/${clientId}`);
    } else if (saveError) {
      showToast.error(saveError);
    }
  };

  const handleGenerateKey = async () => {
    if (!clientId) return;
    const key = await generateEncryptionKey();
    setClientKey(clientId, key);
    setEncKey(key);
    setShowQR(true);
    showToast.success("Encryption key generated");
  };

  const handleRegenerateKey = async () => {
    setConfirmRegenerate(false);
    await handleGenerateKey();
  };

  const handleImportKey = () => {
    if (!clientId) return;
    const trimmed = keyInput.trim();
    if (!trimmed) {
      showToast.error("Please enter an encryption key");
      return;
    }
    if (!isValidEncryptionKey(trimmed)) {
      showToast.error("Invalid encryption key format");
      return;
    }
    setClientKey(clientId, trimmed);
    setEncKey(trimmed);
    setKeyInput("");
    setShowImportSection(false);
    showToast.success("Encryption key imported successfully");
  };

  const handleScanResult = (data: string) => {
    setShowScanner(false);
    if (!clientId) return;
    const trimmed = data.trim();
    if (!isValidEncryptionKey(trimmed)) {
      showToast.error("Scanned QR code does not contain a valid encryption key");
      return;
    }
    setClientKey(clientId, trimmed);
    setEncKey(trimmed);
    setShowImportSection(false);
    showToast.success("Encryption key imported from QR code");
  };

  const handleCopyKey = async () => {
    if (!encKey) return;
    try {
      await navigator.clipboard.writeText(encKey);
      setCopied(true);
      showToast.success("Key copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      showToast.error("Failed to copy key");
    }
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <p className="text-app-danger text-body p-4">{error}</p>;
  if (!client) return null;

  const hasChanges =
    name !== client.name ||
    icon !== client.icon ||
    description !== client.description ||
    isActive !== client.is_active;

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Back button */}
      <button
        type="button"
        onClick={() => navigate(`/admin/${jwt}/${clientId}`)}
        className="flex items-center gap-2 text-sm text-app-text-muted hover:text-app-text transition-colors cursor-pointer"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to {client.name}
      </button>

      {/* Profile Header Preview */}
      <Card className="border-0 shadow-lg overflow-hidden py-0">
        <div className="bg-linear-to-br from-app-primary/20 via-app-accent/10 to-transparent p-6 md:p-8">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-app-surface-raised shadow-md flex items-center justify-center">
              <span className="text-3xl md:text-4xl">{icon}</span>
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-heading font-bold text-app-text truncate">
                {name || "Partner"}
              </h1>
              <p className="text-body text-app-text-muted mt-0.5">
                {description || "No description"}
              </p>
            </div>
            <div className="shrink-0">
              {isActive ? (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-app-success/20 text-app-success text-xs font-medium">
                  <ShieldCheck className="w-3.5 h-3.5" /> Active
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-app-danger/20 text-app-danger text-xs font-medium">
                  <ShieldOff className="w-3.5 h-3.5" /> Inactive
                </span>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Edit Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserCog className="w-5 h-5" />
            Edit Partner
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Icon */}
            <div className="space-y-2">
              <Label>Icon</Label>
              <IconPicker value={icon} onSelect={setIcon} />
            </div>

            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="client-name">Name</Label>
              <Input
                id="client-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Partner display name"
                maxLength={50}
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="client-desc">Description</Label>
              <Textarea
                id="client-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="A note about this partner..."
                maxLength={200}
                rows={3}
              />
            </div>

            {/* Active Toggle */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-app-surface-raised">
              <div>
                <Label htmlFor="client-active" className="cursor-pointer">
                  Active Status
                </Label>
                <p className="text-xs text-app-text-muted mt-0.5">
                  {isActive
                    ? "Partner can log in and use the app"
                    : "Partner is blocked from logging in"}
                </p>
              </div>
              <button
                id="client-active"
                type="button"
                role="switch"
                aria-checked={isActive}
                onClick={() => setIsActive(!isActive)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${
                  isActive ? "bg-app-success" : "bg-app-border"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    isActive ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>

            {/* Save Button */}
            <Button
              type="submit"
              disabled={saving || !hasChanges}
              className="w-full"
            >
              {saving ? (
                <span className="flex items-center gap-2">
                  <LoadingSpinner /> Saving...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Save className="w-4 h-4" /> Save Changes
                </span>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Encryption Key Management */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="w-5 h-5" />
            Encryption Key
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-app-text-muted">
            Generate an encryption key for end-to-end encrypted messages. Share
            it with your partner via QR code or copy. The key is stored in your
            browser only — the server never sees it.
          </p>

          {encKey ? (
            <>
              {/* Key display */}
              <div className="flex items-center gap-2 p-3 rounded-lg bg-app-surface-raised border border-app-border">
                <code className="flex-1 text-sm font-mono text-app-text break-all select-all">
                  {encKey}
                </code>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleCopyKey}
                  className="shrink-0"
                >
                  {copied ? (
                    <Check className="w-4 h-4 text-app-success" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
              </div>

              {/* Action buttons */}
              <div className="flex gap-2 flex-wrap">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowQR(!showQR)}
                  className="flex-1"
                >
                  <QrCode className="w-4 h-4 mr-2" />
                  {showQR ? "Hide QR" : "Show QR"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setConfirmRegenerate(true)}
                  className="flex-1"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Regenerate
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowImportSection(!showImportSection);
                    setShowScanner(false);
                    setKeyInput("");
                  }}
                  className="flex-1"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Import Key
                  {showImportSection ? (
                    <ChevronUp className="w-3 h-3 ml-1" />
                  ) : (
                    <ChevronDown className="w-3 h-3 ml-1" />
                  )}
                </Button>
              </div>

              {/* QR Code display */}
              {showQR && (
                <div className="flex flex-col items-center gap-3 p-6 rounded-lg bg-white">
                  <QRCodeSVG
                    value={encKey}
                    size={200}
                    level="M"
                    includeMargin
                  />
                  <p className="text-xs text-gray-500">
                    Scan this QR code on the partner's device to import the key
                  </p>
                </div>
              )}

              {/* Import existing key section */}
              {showImportSection && (
                <div className="space-y-3 pt-1 border-t border-app-border">
                  <p className="text-sm text-app-text-muted">
                    Replace the current key by pasting an existing key or scanning a QR code.
                  </p>
                  <div className="space-y-2">
                    <Label htmlFor="admin-key-input">Paste Encryption Key</Label>
                    <div className="flex gap-2">
                      <Input
                        id="admin-key-input"
                        value={keyInput}
                        onChange={(e) => setKeyInput(e.target.value)}
                        placeholder="Paste base64 key here…"
                        className="font-mono text-sm"
                      />
                      <Button
                        type="button"
                        onClick={handleImportKey}
                        disabled={!keyInput.trim()}
                      >
                        <Save className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-px bg-app-border" />
                    <span className="text-xs text-app-text-muted">or</span>
                    <div className="flex-1 h-px bg-app-border" />
                  </div>
                  {showScanner ? (
                    <QRScanner
                      onScan={handleScanResult}
                      onClose={() => setShowScanner(false)}
                    />
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowScanner(true)}
                      className="w-full"
                    >
                      <QrCode className="w-4 h-4 mr-2" />
                      Scan QR Code
                    </Button>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="space-y-4">
              <Button
                type="button"
                onClick={handleGenerateKey}
                className="w-full"
              >
                <Key className="w-4 h-4 mr-2" />
                Generate Encryption Key
              </Button>

              {/* Divider */}
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-app-border" />
                <span className="text-xs text-app-text-muted">or import existing</span>
                <div className="flex-1 h-px bg-app-border" />
              </div>

              {/* Paste import */}
              <div className="space-y-2">
                <Label htmlFor="admin-key-input">Paste Encryption Key</Label>
                <div className="flex gap-2">
                  <Input
                    id="admin-key-input"
                    value={keyInput}
                    onChange={(e) => setKeyInput(e.target.value)}
                    placeholder="Paste base64 key here…"
                    className="font-mono text-sm"
                  />
                  <Button
                    type="button"
                    onClick={handleImportKey}
                    disabled={!keyInput.trim()}
                  >
                    <Save className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Divider */}
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-app-border" />
                <span className="text-xs text-app-text-muted">or</span>
                <div className="flex-1 h-px bg-app-border" />
              </div>

              {/* QR Scanner */}
              {showScanner ? (
                <QRScanner
                  onScan={handleScanResult}
                  onClose={() => setShowScanner(false)}
                />
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowScanner(true)}
                  className="w-full"
                >
                  <QrCode className="w-4 h-4 mr-2" />
                  Scan QR Code
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirm Regenerate Dialog */}
      <ConfirmDialog
        open={confirmRegenerate}
        onCancel={() => setConfirmRegenerate(false)}
        title="Regenerate Encryption Key?"
        description="This will replace the current key. The partner will need to import the new key to continue reading encrypted messages. Old messages encrypted with the previous key may become unreadable."
        confirmLabel="Regenerate"
        destructive
        onConfirm={handleRegenerateKey}
      />
    </div>
  );
}
